import React, { useContext } from 'react'

import {
  Box,
  Divider,
  Drawer,
  Hidden,
  IconButton,
  makeAppStyles,
  MenuOption,
  RoundedPlainTextButton,
  RoundedPlainTextButtonMenu,
  Typography,
} from '@percept/mui'

import { ArrowDropDown, Menu, Settings } from '@percept/mui/icons'

import { NavigationTabs } from 'components/NavigationTabs'

import { ResourceListing } from './ResourceListing'

import { WebinarListing, WebinarListingProps } from './WebinarListing'

import { useToggle, useUrlState } from '@percept/hooks'

import { mediaAcademyResouceCategoryLabels } from './lib'

import { Breakpoint } from '@material-ui/core/styles/createBreakpoints'

import { MediaAcademyResourceCategory, MediaAcademyWebinarCategory } from './typings'
import { Link } from 'react-router-dom'
import { UserPrivilegeContext } from '@percept/app-components'
import { userHasOrgPrivileges } from '@percept/utils'
import { VODAFONE_GLOBAL_ID } from 'vodafoneMarkets'
import { capitalize } from 'lodash-es'


type MediaAcademyView = (
  | 'upcomingWebinars'
  | 'pastWebinars'
)

const tabOptions: MenuOption<MediaAcademyView>[] = [
  {
    value: 'upcomingWebinars',
    label: 'Upcoming Webinars',
  },
  {
    value: 'pastWebinars',
    label: 'Past Webinars',
  },
]


type MediaAcademyUrlState = {
  view: MediaAcademyView
  resourceCategory?: MediaAcademyResourceCategory | null
  webinarCategory?: MediaAcademyWebinarCategory | null
}

const getWebinarListingProps = (state: MediaAcademyUrlState): WebinarListingProps => {
  const now = new Date()
  if( state.view === 'upcomingWebinars' ){
    return {
      order: 'ASC',
      start: now,
      descriptor: 'upcoming',
      category: state.webinarCategory,
    }
  }
  return {
    order: 'DESC',
    end: now,
    descriptor: 'past',
    category: state.webinarCategory,
  }
}


const drawerWidths: Record<Breakpoint, number> = {
  xs: 360,
  sm: 400,
  md: 240,
  lg: 300,
  xl: 400,
}

const useStyles = makeAppStyles( theme => ({
  root: {
    padding: theme.spacing(3, 3, 0, 3),
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  container: {
    display: 'flex',
    marginTop: theme.spacing(3),
    flexGrow: 1,
  },
  navBarContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(5),
  },
  tabsContainer: {
    flexGrow: 1,
  },
  toolbarButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  mediaAcademyContent: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    paddingLeft: theme.spacing(5),
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 0,
    },
  },
  drawer: {
    backgroundColor: theme.palette.background.default,
    maxHeight: `calc(100vh - ${theme.spacing(2)}px)`,
    [theme.breakpoints.only('xl')]: {
      width: drawerWidths.xl,
      flexShrink: 0,
    },
    [theme.breakpoints.only('lg')]: {
      width: drawerWidths.lg,
      flexShrink: 0,
    },
    [theme.breakpoints.only('md')]: {
      width: drawerWidths.md,
      flexShrink: 0,
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  drawerTitle: {
    height: 48,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.875rem',
  },
  drawerContent: {
    paddingTop: theme.spacing(2),
    overflowY: 'auto',
    maxHeight: `calc(100% - ${theme.spacing(8)}px)`,
  },
  drawerPaper: {
    [theme.breakpoints.down('sm')]: {
      width: drawerWidths.sm,
    },
    [theme.breakpoints.down('xs')]: {
      width: drawerWidths.xs,
    },
  },
  resourceCategorySelect: {
    height: 24,
  },
  webinarCategorySelect: {
    marginLeft: 'auto',
    marginBottom: theme.spacing(3),
  },
}))




export const MediaAcademy = (): JSX.Element => {

  const privileges = useContext(UserPrivilegeContext)

  const adminEnabled = userHasOrgPrivileges(
    ['vfMediaWizard.mediaAcademy.edit'],
    VODAFONE_GLOBAL_ID,
    privileges.org_privileges,
  )

  const [state, setState] = useUrlState<MediaAcademyUrlState>({
    view: 'upcomingWebinars',
    resourceCategory: undefined,
    webinarCategory: undefined,
  })

  const [drawerOpen, toggleDrawer] = useToggle(false)

  const handleDrawerToggle = (): void => toggleDrawer()

  const classes = useStyles()

  const listingProps = getWebinarListingProps(state)

  const drawerContent = (
    <>
      <Box px={2} display='flex' alignItems='center' justifyContent='space-between'>
        <Typography className={classes.drawerTitle} variant='h5'>Resources</Typography>
        <RoundedPlainTextButtonMenu
          TriggerProps={{
            variant: 'contained',
            color: 'secondary',
            size: 'small',
            endIcon: <ArrowDropDown />,
            classes: {
              root: classes.resourceCategorySelect,
            },
          }}
          value={state.resourceCategory || null}
          label={
            !state.resourceCategory ?
              'All' :
              mediaAcademyResouceCategoryLabels[state.resourceCategory]
          }
          options={
            [
              {
                value: null,
                label: 'All',
              },
              {
                value: 'PLAYBOOK',
                label: mediaAcademyResouceCategoryLabels.PLAYBOOK,
              },
              {
                value: 'WHITEPAPER',
                label: mediaAcademyResouceCategoryLabels.WHITEPAPER,
              },
            ] as MenuOption<MediaAcademyResourceCategory>[]
          }
          onChange={(_, resourceCategory): void => {
            setState({ resourceCategory: resourceCategory || undefined })
          }} />

      </Box>
      <Divider />
      <div className={classes.drawerContent}>
        <ResourceListing
          category={state.resourceCategory} />
      </div>
    </>
  )

  return (
    <div className={classes.root}>
      <Box display='flex' justifyContent='space-between'>
        <Typography variant='h3'>Media Academy</Typography>

        { adminEnabled && (
          <Link to='/wizard/optimisation/media-academy/admin'>
            <RoundedPlainTextButton
              variant='contained'
              size='small'
              startIcon={<Settings />}>
              Admin
            </RoundedPlainTextButton>
          </Link>
        )}
      </Box>

      <div className={classes.container}>

        <Hidden mdUp implementation="css">
          <Drawer
            variant="temporary"
            open={drawerOpen}
            onClose={handleDrawerToggle}
            classes={{
              paper: classes.drawerPaper,
            }}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
          >
            {drawerContent}
          </Drawer>
        </Hidden>

        <div className={classes.drawer}>
          {drawerContent}
        </div>

        <div className={classes.mediaAcademyContent}>
          <div className={classes.navBarContainer}>
            <IconButton
              className={classes.toolbarButton}
              onClick={handleDrawerToggle}>
              <Menu />
            </IconButton>

            <div className={classes.tabsContainer}>
              <NavigationTabs
                value={state.view}
                options={tabOptions}
                onChange={(view): void => {
                  setState({ view })
                }} />
            </div>
          </div>

          <RoundedPlainTextButtonMenu
            TriggerProps={{
              variant: 'contained',
              color: 'secondary',
              endIcon: <ArrowDropDown />,
              classes: {
                root: classes.webinarCategorySelect,
              },
            }}
            value={state.webinarCategory || null}
            label={
              !state.webinarCategory ?
                'All Categories' :
                capitalize(state.webinarCategory)
            }
            options={
              [
                {
                  value: null,
                  label: 'All Categories',
                },
                ...(['ENABLE', 'EXPAND', 'EXTRACT', 'MARTECH'] as MediaAcademyWebinarCategory[]).map( value => ({
                  value,
                  label: capitalize(value),
                })),
              ] as MenuOption<MediaAcademyWebinarCategory>[]
            }
            onChange={(_, webinarCategory): void => {
              setState({ webinarCategory: webinarCategory || undefined })
            }} />

          <WebinarListing {...listingProps} />

        </div>
      </div>
    </div>
  )
}
